import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { TrendingUp, RefreshCw, DollarSign, CheckCircle, Percent, Users, Loader2, BarChart3, Wallet, ChevronDown, Filter } from 'lucide-react'
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts'
import { useBot } from '@/shared/hooks/useBots'
import { useBotStatistics } from '@/shared/hooks/useBotStatistics'
import { useTelegram } from '@/shared/hooks/useTelegram'
import { useBackButton } from '@/shared/hooks/useBackButton'
import { formatToMoscowTime } from '@/shared/utils/format'
import type { Granularity, BotDailyMetric, BotHourlyMetric, SourceFilter } from '@/shared/types/statistics'

type TimeRange = '24h' | '7d' | '30d'
type MetricType = 'traffic' | 'revenue'

export default function BotStatistics() {
  const { botId } = useParams<{ botId: string }>()
  const { hapticFeedback, hapticNotification } = useTelegram()
  const { data: bot, isLoading: botLoading } = useBot(botId)

  const [timeRange, setTimeRange] = useState<TimeRange>('24h')
  const [metricType, setMetricType] = useState<MetricType>('traffic')
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all')
  const [forceRefresh, setForceRefresh] = useState(false)
  const [isTimeRangeChanging, setIsTimeRangeChanging] = useState(false)
  const [isSourceFilterOpen, setIsSourceFilterOpen] = useState(false)

  // Calculate date range and granularity based on selected time range
  const getDateRange = (range: TimeRange): { start: string; end: string; granularity: Granularity } => {
    const end = new Date()
    const start = new Date()

    switch (range) {
      case '24h':
        start.setDate(start.getDate() - 1)
        return {
          start: start.toISOString().split('T')[0],
          end: end.toISOString().split('T')[0],
          granularity: 'hour',
        }
      case '7d':
        start.setDate(start.getDate() - 7)
        return {
          start: start.toISOString().split('T')[0],
          end: end.toISOString().split('T')[0],
          granularity: 'day',
        }
      case '30d':
        start.setDate(start.getDate() - 30)
        return {
          start: start.toISOString().split('T')[0],
          end: end.toISOString().split('T')[0],
          granularity: 'day',
        }
    }
  }

  const dateRange = getDateRange(timeRange)
  const { data: stats, isLoading, error, refetch, isFetching } = useBotStatistics(
    botId,
    dateRange.start,
    dateRange.end,
    dateRange.granularity,
    { enabled: true, refresh: forceRefresh }
  )

  // Extract current source data from all_sources map (frontend filtering)
  const currentSourceData = stats?.all_sources?.[sourceFilter]

  // Auto-manage back button
  useBackButton()

  // Setup page background
  useEffect(() => {
    window.scrollTo(0, 0)
    document.body.style.backgroundColor = '#1e2832'

    return () => {
      document.body.style.backgroundColor = ''
    }
  }, [])

  // Transform time series data for charts
  const getChartData = () => {
    if (!currentSourceData?.time_series || currentSourceData.time_series.length === 0) return []

    return currentSourceData.time_series.map((item: BotDailyMetric | BotHourlyMetric) => {
      // Determine if it's hourly or daily data
      const isHourly = 'metric_timestamp' in item
      const timeKey = isHourly ? item.metric_timestamp : (item as BotDailyMetric).metric_date

      // Format time label in Moscow timezone
      const granularity = isHourly ? 'hour' : 'day'
      const timeLabel = formatToMoscowTime(timeKey, granularity)

      return {
        time: timeLabel,
        fullTime: timeKey,
        tasks: item.total_tasks_created,
        completed: item.tasks_completed,
        failed: item.tasks_failed,
        verifying: item.tasks_verifying,
        users: item.unique_users,
        revenue: item.revenue_generated,
        avgReward: item.avg_task_reward,
      }
    })
  }

  // Calculate "X minutes ago" display from backend age_seconds
  const getTimeAgo = () => {
    if (!stats) return 'Нет данных'
    if (!stats.cache_metadata?.last_updated) return 'Только что'

    const ageSeconds = stats.cache_metadata.age_seconds

    if (ageSeconds < 60) return 'Только что'
    if (ageSeconds < 3600) return `${Math.floor(ageSeconds / 60)} мин назад`
    if (ageSeconds < 86400) return `${Math.floor(ageSeconds / 3600)} ч назад`
    return `${Math.floor(ageSeconds / 86400)} д назад`
  }

  // Manual refresh handler - forces backend cache bypass
  const handleRefresh = async () => {
    hapticFeedback('medium')
    setForceRefresh(true)
    try {
      await refetch()
      hapticNotification('success')
    } catch (err) {
      hapticNotification('error')
    } finally {
      // Reset refresh flag after fetch completes
      setForceRefresh(false)
    }
  }

  // Handle time range change
  const handleTimeRangeChange = (range: TimeRange) => {
    hapticFeedback('light')
    setIsTimeRangeChanging(true)
    setTimeRange(range)
  }

  // Reset loading state when data is loaded
  useEffect(() => {
    if (!isFetching && isTimeRangeChanging) {
      setIsTimeRangeChanging(false)
    }
  }, [isFetching, isTimeRangeChanging])

  // Handle metric type change
  const handleMetricTypeChange = (type: MetricType) => {
    hapticFeedback('light')
    setMetricType(type)
  }

  // Handle source filter change
  const handleSourceFilterChange = (source: SourceFilter) => {
    hapticFeedback('light')
    setSourceFilter(source)
    setIsSourceFilterOpen(false) // Close the dropdown after selection
  }

  // Toggle source filter menu
  const toggleSourceFilter = () => {
    hapticFeedback('light')
    setIsSourceFilterOpen(!isSourceFilterOpen)
  }

  if (botLoading || !bot) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-tg-bg">
        <Loader2 className="w-8 h-8 animate-spin text-tg-button" />
      </div>
    )
  }

  const timeRanges = [
    { value: '24h' as const, label: '24ч', icon: '⏰' },
    { value: '7d' as const, label: '7д', icon: '📅' },
    { value: '30d' as const, label: '30д', icon: '📅' },
  ]

  const chartData = getChartData()
  const hasChartData = chartData.length > 0

  return (
    <div className="min-h-screen bg-tg-bg pb-24 pt-[96px]">
      {/* Header */}
      <div className="bg-tg-secondary-bg border-b border-tg-secondary-bg">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#059669]/10 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-[#059669]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-tg-text">Статистика</h1>
              <p className="text-sm text-tg-hint mt-1">@{bot.username}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-3 py-4 space-y-3">
        {/* Time Range Selector */}
        <div className="bg-tg-secondary-bg rounded-2xl shadow-sm overflow-hidden p-3">
          <div className="flex gap-1 p-1 bg-tg-bg rounded-xl">
            {timeRanges.map((range) => (
              <button
                key={range.value}
                onClick={() => handleTimeRangeChange(range.value)}
                className={`flex-1 py-2.5 rounded-lg font-medium text-sm transition-all ${
                  timeRange === range.value
                    ? 'bg-tg-button text-white shadow-lg'
                    : 'text-tg-hint hover:text-tg-text'
                }`}
              >
                <span className="text-base mr-1">{range.icon}</span>
                {range.label}
              </button>
            ))}
          </div>
        </div>

        {/* Refresh Section */}
        <div className="bg-tg-secondary-bg rounded-2xl shadow-sm overflow-hidden p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-tg-text">Последнее обновление</p>
              <p className="text-xs text-tg-hint mt-1">
                {getTimeAgo()}
              </p>
            </div>
            <button
              onClick={handleRefresh}
              disabled={isFetching}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${
                isFetching
                  ? 'bg-tg-hint/20 text-tg-hint cursor-not-allowed'
                  : 'bg-tg-button text-white hover:shadow-lg active:scale-95'
              }`}
            >
              <RefreshCw
                className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`}
              />
              {isFetching ? 'Загрузка...' : 'Обновить'}
            </button>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
            <div className="text-4xl mb-3">❌</div>
            <h3 className="text-lg font-semibold text-red-900 mb-2">Не удалось загрузить статистику</h3>
            <p className="text-sm text-red-600 mb-4">{error.message}</p>
            <button
              onClick={handleRefresh}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Повторить
            </button>
          </div>
        )}

        {/* Loading Skeleton */}
        {isLoading && (
          <div className="grid grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="bg-tg-secondary-bg rounded-2xl p-4 h-32 animate-pulse"
              />
            ))}
          </div>
        )}

        {/* Summary Cards */}
        {currentSourceData?.summary && (
          <div className="grid grid-cols-2 gap-3">
            {/* Total Revenue */}
            <div className="bg-tg-secondary-bg rounded-2xl p-3 shadow-sm border-2 border-[#059669]/20">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-[#059669]/10 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-[#059669]" />
                </div>
                <span className="text-xs text-tg-hint">Доход</span>
              </div>
              <div className="text-xl font-bold text-tg-text">
                {currentSourceData.summary.total_revenue.toFixed(2)} ₽
              </div>
              <div className="text-xs text-tg-hint mt-0.5">
                {currentSourceData.summary.avg_daily_revenue.toFixed(2)} ₽/день
              </div>
            </div>

            {/* Tasks (All/Completed/Verifying) */}
            <div className="bg-tg-secondary-bg rounded-2xl p-3 shadow-sm border-2 border-[#3b82f6]/20">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-[#3b82f6]/10 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-[#3b82f6]" />
                </div>
                <span className="text-xs text-tg-hint">Задачи</span>
              </div>
              <div className="text-xl font-bold text-tg-text">
                {currentSourceData.summary.total_tasks}
              </div>
              <div className="flex flex-col gap-0.5 mt-1">
                <div className="text-xs text-tg-hint">
                  {currentSourceData.summary.total_completed} выполнено
                </div>
                <div className="text-xs text-tg-hint">
                  {currentSourceData.summary.total_verifying} на проверке
                </div>
                <div className="text-xs text-tg-hint">
                  {currentSourceData.summary.total_failed} не выполнено
                </div>
              </div>
            </div>

            {/* Completion Rate */}
            <div className="bg-tg-secondary-bg rounded-2xl p-3 shadow-sm border-2 border-[#f59e0b]/20">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-[#f59e0b]/10 rounded-lg flex items-center justify-center">
                  <Percent className="w-5 h-5 text-[#f59e0b]" />
                </div>
                <span className="text-xs text-tg-hint">Конверсия</span>
              </div>
              <div className="text-xl font-bold text-tg-text">
                {(currentSourceData.summary.avg_completion_rate * 100).toFixed(1)}%
              </div>
              <div className="text-xs text-tg-hint mt-0.5">
                среднее выполнение
              </div>
            </div>

            {/* Active Users */}
            <div className="bg-tg-secondary-bg rounded-2xl p-3 shadow-sm border-2 border-[#8b5cf6]/20">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-[#8b5cf6]/10 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#8b5cf6]" />
                </div>
                <span className="text-xs text-tg-hint">Пользователи</span>
              </div>
              <div className="text-xl font-bold text-tg-text">
                {currentSourceData.summary.total_unique_users}
              </div>
              <div className="text-xs text-tg-hint mt-0.5">
                {currentSourceData.summary.total_new_users} новых
              </div>
            </div>
          </div>
        )}

        {/* Source Filter - Collapsible */}
        <div className="bg-tg-secondary-bg rounded-2xl shadow-sm overflow-hidden">
          {/* Toggle Button */}
          <button
            onClick={toggleSourceFilter}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-tg-bg/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-tg-hint" />
              <span className="text-sm font-medium text-tg-text">Источник трафика</span>
              <span className="text-base flex h-5 w-5 items-center justify-center">
                {sourceFilter === 'tgrass' ? (
                  <img src="/images/integrations/tgrass.svg" alt="" className="h-5 w-5 rounded object-cover" />
                ) : ({
                    all: '📊',
                    internal: '🏠',
                    flyer: '✈️',
                    subgram: '📱',
                    fdu: 'F',
                    tgrass: 'T'
                  }[sourceFilter])}
              </span>
              <span className="text-xs text-tg-hint">
                {{
                  all: 'Все',
                  internal: 'Внутренние',
                  flyer: 'Flyer',
                  subgram: 'Subgram',
                  fdu: 'FDU',
                  tgrass: 'Tgrass'
                }[sourceFilter]}
              </span>
            </div>
            <ChevronDown className={`w-4 h-4 text-tg-hint transition-transform duration-200 ${isSourceFilterOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Collapsible Filter Grid */}
          {isSourceFilterOpen && (
            <div className="px-3 pb-3 pt-1 animate-in fade-in slide-in-from-top-2 duration-200">
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'all' as const, label: 'Все', icon: '📊', color: 'from-blue-500/20 to-blue-600/20 border-blue-500/30' },
                  { value: 'internal' as const, label: 'Внутренние', icon: '🏠', color: 'from-green-500/20 to-green-600/20 border-green-500/30' },
                  { value: 'flyer' as const, label: 'Flyer', icon: '✈️', color: 'from-purple-500/20 to-purple-600/20 border-purple-500/30' },
                  { value: 'subgram' as const, label: 'Subgram', icon: '📱', color: 'from-orange-500/20 to-orange-600/20 border-orange-500/30' },
                  { value: 'fdu' as const, label: 'FDU', icon: 'F', color: 'from-red-500/20 to-red-600/20 border-red-500/30' },
                  { value: 'tgrass' as const, label: 'Tgrass', icon: 'T', color: 'from-emerald-500/20 to-emerald-600/20 border-emerald-500/30' },
                ].map((source) => (
                  <button
                    key={source.value}
                    onClick={() => handleSourceFilterChange(source.value)}
                    className={`py-3 px-3 rounded-xl font-medium text-sm transition-all flex flex-col items-center justify-center gap-1 border active:scale-95 ${
                      sourceFilter === source.value
                        ? `bg-gradient-to-br ${source.color} shadow-lg scale-[1.02]`
                        : 'bg-tg-bg border-transparent text-tg-hint hover:text-tg-text hover:border-tg-hint/20'
                    }`}
                  >
                    {source.value === 'tgrass' ? (
                      <img src="/images/integrations/tgrass.svg" alt="" className="h-7 w-7 rounded-md object-cover" />
                    ) : (
                      <span className="text-2xl">{source.icon}</span>
                    )}
                    <span className="text-xs font-semibold">{source.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Chart Section */}
        {currentSourceData && hasChartData && (
          <>
            {/* Metric Type Toggle */}
            <div className="bg-tg-secondary-bg rounded-2xl shadow-sm overflow-hidden p-3">
              <div className="flex gap-2">
                <button
                  onClick={() => handleMetricTypeChange('traffic')}
                  className={`flex-1 py-3 px-4 rounded-xl font-medium text-sm transition-all flex items-center justify-center gap-2 ${
                    metricType === 'traffic'
                      ? 'bg-[#3b82f6] text-white shadow-lg'
                      : 'bg-tg-bg text-tg-hint hover:text-tg-text'
                  }`}
                >
                  <BarChart3 className="w-4 h-4" />
                  Трафик
                </button>
                <button
                  onClick={() => handleMetricTypeChange('revenue')}
                  className={`flex-1 py-3 px-4 rounded-xl font-medium text-sm transition-all flex items-center justify-center gap-2 ${
                    metricType === 'revenue'
                      ? 'bg-[#059669] text-white shadow-lg'
                      : 'bg-tg-bg text-tg-hint hover:text-tg-text'
                  }`}
                >
                  <Wallet className="w-4 h-4" />
                  Доход
                </button>
              </div>
            </div>

            {/* Chart Container */}
            {isTimeRangeChanging ? (
              <div className="bg-tg-secondary-bg rounded-2xl shadow-sm overflow-hidden p-3">
                <div className="h-[440px] animate-pulse flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-tg-button" />
                </div>
              </div>
            ) : (
              <div className="bg-tg-secondary-bg rounded-2xl shadow-sm overflow-hidden p-3">
                <h3 className="text-sm font-semibold text-tg-text mb-2 px-1">
                  {metricType === 'traffic' ? 'График трафика' : 'График дохода'}
                </h3>

                <div className="w-full h-[400px] -ml-2">
                  <ResponsiveContainer width="100%" height="100%" key={`${timeRange}-${metricType}`}>
                  {metricType === 'traffic' ? (
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#2a3441" />
                      <XAxis
                        dataKey="time"
                        stroke="#6b7280"
                        tick={{ fill: '#9ca3af', fontSize: 12 }}
                      />
                      <YAxis
                        stroke="#6b7280"
                        tick={{ fill: '#9ca3af', fontSize: 11 }}
                        width={40}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#1e2832',
                          border: '1px solid #374151',
                          borderRadius: '8px',
                          color: '#f9fafb',
                        }}
                        labelStyle={{ color: '#9ca3af' }}
                      />
                      <Legend
                        wrapperStyle={{ color: '#9ca3af' }}
                        iconType="line"
                      />
                      <Line
                        type="monotone"
                        dataKey="tasks"
                        stroke="#3b82f6"
                        strokeWidth={2}
                        name="Всего задач"
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="completed"
                        stroke="#059669"
                        strokeWidth={2}
                        name="Выполнено"
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="verifying"
                        stroke="#f59e0b"
                        strokeWidth={2}
                        name="На проверке"
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="failed"
                        stroke="#ef4444"
                        strokeWidth={2}
                        name="Не выполнено"
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="users"
                        stroke="#8b5cf6"
                        strokeWidth={2}
                        name="Пользователи"
                        dot={false}
                      />
                    </LineChart>
                  ) : (
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#059669" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#059669" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#2a3441" />
                      <XAxis
                        dataKey="time"
                        stroke="#6b7280"
                        tick={{ fill: '#9ca3af', fontSize: 12 }}
                      />
                      <YAxis
                        stroke="#6b7280"
                        tick={{ fill: '#9ca3af', fontSize: 11 }}
                        width={40}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#1e2832',
                          border: '1px solid #374151',
                          borderRadius: '8px',
                          color: '#f9fafb',
                        }}
                        labelStyle={{ color: '#9ca3af' }}
                        formatter={(value: number) => `${value.toFixed(2)} ₽`}
                      />
                      <Legend
                        wrapperStyle={{ color: '#9ca3af' }}
                      />
                      <Area
                        type="monotone"
                        dataKey="revenue"
                        stroke="#059669"
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#revenueGradient)"
                        name="Доход (₽)"
                      />
                    </AreaChart>
                  )}
                </ResponsiveContainer>
              </div>
            </div>
            )}
          </>
        )}

        {/* Empty State */}
        {!currentSourceData && !isLoading && !error && (
          <div className="bg-tg-secondary-bg rounded-2xl p-8 text-center">
            <div className="text-6xl mb-4">📊</div>
            <h3 className="text-lg font-semibold text-tg-text mb-2">Нет данных</h3>
            <p className="text-sm text-tg-hint mb-4">
              За выбранный период нет данных для отображения
            </p>
          </div>
        )}

        {/* No Data State for Chart */}
        {currentSourceData && !hasChartData && (
          <div className="bg-tg-secondary-bg rounded-2xl p-8 text-center">
            <div className="text-4xl mb-3">📉</div>
            <h3 className="text-lg font-semibold text-tg-text mb-2">Нет данных</h3>
            <p className="text-sm text-tg-hint">
              За выбранный период нет данных для отображения
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
